import React from 'react'
import ReactDOM from 'react-dom/client'
import {
    createHashRouter,
    defer,
    Navigate,
    RouterProvider,
} from 'react-router-dom'

import './index.css'
import reportWebVitals from './reportWebVitals'

import RootLayout from './pages/RootLayout'
import ErrorPage from './pages/ErrorPage'

import HomePage from './pages/Home/HomePage'

import AuthLayout from './pages/Auth/AuthLayout'
import LoginForm from './pages/Auth/LoginForm'
import RestoreForm from './pages/Auth/RestoreForm'
import RegisterForm, { loadOrganization } from './pages/Auth/RegisterForm'
import SearchForm from './pages/Auth/SearchForm'
import ResetForm from './pages/Auth/ResetForm'

import { getUserData } from './services/auth'

import HubPage from './pages/Hub/HubPage'
import HubLayout from './pages/Hub/HubLayout'
import HubLayoutInner from './pages/Hub/HubLayoutInner'

import OrgHomePage, { loadOrganizationData } from './pages/Hub/Org/Home/Home'
import OrgListsPage from './pages/Hub/Org/Lists'
import OrgPurchaseMethodsPage, {
    loadPurchaseMethods,
} from './pages/Hub/Org/PurchaseMethods'
import OrgStructurePage, { loadStructure } from './pages/Hub/Org/Structure'
import OrgUsersPage, { loadUsers } from './pages/Hub/Org/Users'
import OrgRolesPage, { loadRole } from './pages/Hub/Org/Roles'

import PlanListPage, { loadPlanList } from './pages/Hub/Plans'
import PlanPositionListPage from './pages/Hub/Plans/Positions'
import PlanPositionEditPage, {
    loadPlanPosition,
} from './pages/Hub/Plans/Positions/Edit'
import OrgUserViewOrEditPage, {
    loadUser,
} from './pages/Hub/Org/Users/ViewOrEdit'
import OrgPurchaseMethodViewOrEdit, {
    loadPurchaseMethod,
} from './pages/Hub/Org/PurchaseMethods/ViewOrEdit'
import OrgListMspPage from './pages/Hub/Org/Lists/Msp'
import OrgListEpPage, { loadListEp } from './pages/Hub/Org/Lists/Ep'
import OrgListFxdPage, { loadListFxd } from './pages/Hub/Org/Lists/Fxd'
import OrgHandbookListPage from './pages/Hub/Org/Handbook'
import OrgHandbookOkved2Page from './pages/Hub/Org/Handbook/components/OKVED2'
import OrgHandbookOkpd2Page from './pages/Hub/Org/Handbook/components/OKPD2'

import TemplatePage from './pages/Hub/Org/Study/components/Template'

import ApprovalsPage from './pages/Hub/Plans/Approvals'
import Agreement from './pages/Home/Info/Agreement'
import Info from './pages/Home/Info/Info'

import PublicLayout from './pages/Home/PublicLayout'

import AboutPage from './pages/Home/About/AboutPage'
import AboutUsPage from './pages/Home/About/AboutUsPage'
import CompanyDocsPage from './pages/Home/About/CompanyDocsPage'
import ContactsPage from './pages/Home/About/ContactsPage'

import DocumentsPage from './pages/Home/Documents/DocumentsPage'
import ManualPage from './pages/Home/Documents/ManualPage'
import PrivacyPolicyPage from './pages/Home/Documents/PrivacyPolicyPage'
import UserAgreementPage from './pages/Home/Documents/UserAgreementPage'

import GuidesPage from './pages/Home/Guides/GuidesPage'
import Okpd2Page from './pages/Home/Guides/Okpd2Page'
import Okved2Page from './pages/Home/Guides/Okved2Page'

import PricePage from './pages/Home/PricePage'
import NewHomePage from './pages/Home/NewHomePage'

import ContractListPage from './pages/Hub/Contracts'
import ContractPage, { loadContract } from './pages/Hub/Contracts/Edit'
import ContractPerformanceEditPage, {
    loadPerformances,
} from './pages/Hub/Contracts/Edit/Performances'

import ContractEditPage, {
    loadContractData,
} from './pages/Hub/Contracts/Edit/Contracts'
ContractEditPage

import PurchasePage, { loadPurchase } from './pages/Hub/Purchases/Edit'
import PurchaseEditPage, {
    loadPurchaseData,
} from './pages/Hub/Purchases/Edit/Purchase'
PurchaseEditPage
import PurchaseLotEditPage, {
    loadPurchaseLotData,
} from './pages/Hub/Purchases/Edit/PurchaseLot'

import PerformanceListPage from './pages/Hub/Performances'
import PurchaseListPage from './pages/Hub/Purchases'

import { ThemeProvider } from './components/ThemeContext'

const siteRoutes = [
    {
        path: '/',
        element: <PublicLayout />,
        children: [
            {
                index: true,
                element: <HomePage />,
            },
            {
                path: 'guides',
                element: <GuidesPage />,
                children: [
                    {
                        path: 'okpd2',
                        element: <Okpd2Page />,
                    },
                    {
                        path: 'okved2',
                        element: <Okved2Page />,
                    },
                ],
            },
            {
                path: 'about',
                element: <AboutPage />,
                children: [
                    {
                        path: 'us',
                        element: <AboutUsPage />,
                    },
                    {
                        path: 'companydocs',
                        element: <CompanyDocsPage />,
                    },
                    {
                        path: 'contacts',
                        element: <ContactsPage />,
                    },
                ],
            },
            {
                path: 'docs',
                element: <DocumentsPage />,
                children: [
                    {
                        path: 'manual',
                        element: <ManualPage />,
                    },
                    {
                        path: 'privacy-policy',
                        element: <PrivacyPolicyPage />,
                    },
                    {
                        path: 'user-agreement',
                        element: <UserAgreementPage />,
                    },
                ],
            },
            {
                path: 'price',
                element: <PricePage />,
            },
            {
                path: 'new',
                element: <NewHomePage />,
            },
            {
                path: 'info',
                element: <Info />,
                children: [
                    {
                        path: 'agreement',
                        element: <Agreement />,
                    },
                ],
            },
        ],
    },
    {
        path: 'auth',
        element: <AuthLayout />,
        children: [
            {
                index: true,
                element: <LoginForm />,
            },
            {
                path: 'restore',
                element: <RestoreForm />,
            },
            {
                path: 'reset_password',
                element: <ResetForm />,
            },
            {
                path: 'search',
                element: <SearchForm />,
            },
            {
                path: 'register/:id',
                loader: loadOrganization,
                element: <RegisterForm />,
            },
        ],
    },
]
const systemRoutes = [
    {
        path: 'hub',
        element: <HubLayout />,
        children: [
            {
                element: <HubLayoutInner />,
                children: [
                    {
                        path: 'org/home',
                        element: <OrgHomePage />,
                        loader: loadOrganizationData,
                    },
                    {
                        path: 'org/users',
                        element: <OrgUsersPage />,
                        loader: loadUsers,
                    },
                    {
                        path: 'org/users/:guid',
                        element: <OrgUserViewOrEditPage />,
                        loader: loadUser,
                    },
                    {
                        path: 'org/users/:guid/edit',
                        element: <OrgUserViewOrEditPage />,
                        loader: loadUser,
                    },
                    {
                        path: 'org/structure',
                        element: <OrgStructurePage />,
                        loader: loadStructure,
                    },
                    {
                        path: 'org/roles',
                        element: <OrgRolesPage />,
                        loader: loadRole,
                    },
                    {
                        path: 'org/methods',
                        element: <OrgPurchaseMethodsPage />,
                        loader: loadPurchaseMethods,
                    },
                    {
                        path: 'org/methods/:guid',
                        element: <OrgPurchaseMethodViewOrEdit />,
                        loader: loadPurchaseMethod,
                    },
                    {
                        path: 'org/methods/:guid/edit',
                        element: <OrgPurchaseMethodViewOrEdit />,
                        loader: loadPurchaseMethod,
                    },
                    {
                        path: 'org/lists',
                        element: <OrgListsPage />,
                    },
                    {
                        path: 'org/lists/msp',
                        element: <OrgListMspPage />,
                    },
                    {
                        path: 'org/lists/ep',
                        element: <OrgListEpPage />,
                        loader: loadListEp,
                    },
                    {
                        path: 'org/lists/fxd',
                        element: <OrgListFxdPage />,
                        loader: loadListFxd,
                    },
                    {
                        path: 'org/study/template',
                        element: <TemplatePage />,
                    },
                    {
                        path: 'org/handbook',
                        element: <OrgHandbookListPage />,
                    },
                    {
                        path: 'org/handbook/okpd2',
                        element: <OrgHandbookOkpd2Page />,
                    },
                    {
                        path: 'org/handbook/okved2',
                        element: <OrgHandbookOkved2Page />,
                    },
                    {
                        index: true,
                        element: <HubPage />,
                    },
                    {
                        path: 'plans',
                        loader: loadPlanList,
                        element: <PlanListPage />,
                    },
                    {
                        path: 'plans/:plan_reg_number/positions',
                        element: <PlanPositionListPage />,
                    },
                    {
                        path: 'home',
                        element: <HubPage />,
                    },
                    {
                        path: 'approvals/plans',
                        element: <ApprovalsPage />,
                    },
                    {
                        path: 'contracts',
                        element: <ContractListPage />,
                    },
                    {
                        path: 'performances',
                        element: <PerformanceListPage />,
                    },
                    {
                        path: 'purchases',
                        element: <PurchaseListPage />,
                    },
                ],
            },
            {
                path: 'plans/:plan_reg_number/positions/:action/:pos_guid?',
                loader: loadPlanPosition,
                element: <PlanPositionEditPage />,
            },
            {
                path: 'contract/:contract_reg_number?',
                loader: loadContract,
                element: <ContractPage />,
            },
            {
                path: 'purchase/:purchase_reg_number?',
                loader: loadPurchase,
                element: <PurchasePage />,
            },
            {
                path: 'contract/:contract_reg_number/performances/:perf_action/:perf_guid',
                loader: loadPerformances,
                element: <ContractPerformanceEditPage />,
            },
            {
                path: 'contract/:contract_reg_number/contracts/:contract_action/:contract_guid',
                loader: loadContractData,
                element: <ContractEditPage />,
            },
            {
                path: 'purchase/:purchase_reg_number/purchases/:purchase_action/:purchase_guid',
                loader: loadPurchaseData,
                element: <PurchaseEditPage />,
            },
            {
                path: 'purchase/:purchase_reg_number/purchases/:purchase_action/:purchase_guid/lot/:purchase_lot_guid',
                loader: loadPurchaseLotData,
                element: <PurchaseLotEditPage />,
            },
        ],
    },
]

let routes
if (process.env.REACT_APP_MODE == 'site') {
    routes = siteRoutes
} else if (process.env.REACT_APP_MODE == 'system') {
    routes = [
        ...systemRoutes,
        {
            path: '',
            element: <Navigate to="hub" replace />,
        },
    ]
} else {
    routes = [...siteRoutes, ...systemRoutes]
}

const router = createHashRouter([
    {
        path: '/',
        element: <RootLayout />,
        loader: () => defer({ userPromise: getUserData() }),
        errorElement: <ErrorPage />,
        children: routes,
    },
])

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
    <React.StrictMode>
        <ThemeProvider>
            <RouterProvider router={router} />
        </ThemeProvider>
    </React.StrictMode>
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
